/**
 * RoamerStayModule.ts — Module #4: the Roamer Stay™.
 *
 * A platform module (NOT a core engine): long-term accommodation
 * intelligence — living, not vacation travel. Module pattern rules:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound data ONLY via Core bus events + manual entry:
 *    - LocationChanged (real { location: {...} } payload shape, flat
 *      fallback) → current city context for searches and intelligence.
 *    - HousingUpdated is fusion-bridged and carries only metadata
 *      ({domain, providerId, recordCount}) — it is treated as a refresh
 *      signal; listings are NEVER invented from it.
 *  • Outbound: price-change and availability alerts for favorited listings
 *    published as NotificationSent (kinds stayPriceChange / stayAvailability).
 *  • Honesty: listing attributes are nullable; the Match Score scores only
 *    real data (null when nothing is scorable); housing intelligence derives
 *    exclusively from recorded listings.
 *  • Providers (apartments, coliving, extended stay, verified community
 *    listings, real-estate APIs, property managers, market data) are a
 *    registry of interfaces only — no implementations, per the brief.
 *  • Persistence nomad:stay:v1 with record-by-record sanitization and caps;
 *    deep clones at every read boundary; export + delete supported.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  DEFAULT_HOUSING_PROFILE,
  DEFAULT_STAY_PRIVACY,
  PAYMENT_STATUSES,
  type NewStayRecordInput,
  type StayDashboard,
  type StayPrivacy,
  type StayRecord,
  type StayStats,
  type StayTimeline,
  EMPTY_AMENITIES,
  HOUSING_TYPES,
  type HousingIntelligence,
  type HousingProfile,
  type RoamerMatchScore,
  type SharedListing,
  type Shortlist,
  type StayAmenities,
  type StayComparison,
  type StayExport,
  type StayListing,
  type StayProvider,
  type StaySearchFilters,
} from './models';
import { computeMatchScore } from './match';
import { computeHousingIntelligence } from './intelligence';

const STORAGE_KEY = 'nomad:stay:v1';
const MODULE_ID = 'roamer-stay-module';
const MAX_LISTINGS = 1000;
const MAX_PRICE_HISTORY = 100;
const MAX_SHORTLISTS = 50;
const MAX_STAY_RECORDS = 500;
const DAY_MS = 24 * 60 * 60 * 1000;
const AVG_MONTH_DAYS = 30.44;

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// ── Provider registry (future integrations — interfaces only) ────────────────

const stayProviders = new Map<string, StayProvider>();

export function registerStayProvider(p: StayProvider): boolean {
  if (stayProviders.has(p.id)) return false;
  stayProviders.set(p.id, p);
  return true;
}

export function getStayProviders(): StayProvider[] {
  return Array.from(stayProviders.values());
}

// ── Persistence shape ─────────────────────────────────────────────────────────

interface PersistedState {
  profile: HousingProfile;
  currentCity: string | null;
  listings: StayListing[];
  favoriteListingIds: string[];
  favoriteNeighborhoods: string[];
  shortlists: Shortlist[];
  /** Build 2.03.0 — accommodation records (manual tracking only). */
  stayRecords?: StayRecord[];
  stayPrivacy?: StayPrivacy;
}

export type NewListingInput = Omit<StayListing, 'id' | 'priceHistory' | 'addedAt' | 'amenities' | 'source'> & {
  amenities?: Partial<StayAmenities>;
  source?: StayListing['source'];
};

export class RoamerStayModule {
  private profile: HousingProfile = { ...DEFAULT_HOUSING_PROFILE };
  private currentCity: string | null = null;
  private listings: StayListing[] = [];
  private favoriteListingIds: string[] = [];
  private favoriteNeighborhoods: string[] = [];
  private shortlists: Shortlist[] = [];
  private stayRecords: StayRecord[] = [];
  private stayPrivacy: StayPrivacy = { ...DEFAULT_STAY_PRIVACY };
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();
  /** Bumped on HousingUpdated so future provider sync can react; no data invented. */
  private refreshSignals = 0;

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration ────────────────────────────────────────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Stay™',
      version: '1.0.0',
      capabilities: ['housing-search', 'match-score', 'stay-comparison', 'housing-intelligence', 'favorites'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const safe = (handler: (e: CoreEvent) => void) => (e: CoreEvent) => {
      try {
        handler(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    };
    this.unsubscribes.push(core.on('LocationChanged', safe((e) => this.onLocationChanged(e)), 'module:stay'));
    // Own-source guard: this module PUBLISHES HousingUpdated when accommodation
    // records change (ECOSYSTEM SYNCHRONIZATION™) — never consume our own
    // publication, or every record edit would count as an external refresh.
    this.unsubscribes.push(core.on('HousingUpdated', safe((e) => {
      if (e.source === MODULE_ID) return;
      this.refreshSignals += 1;
    }), 'module:stay'));
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  getRefreshSignalCount(): number {
    return this.refreshSignals;
  }

  /** Whether attachToCore() has run (diagnostics parity with the other modules). */
  isAttached(): boolean {
    return this.core != null;
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city);
    if (!city || city === this.currentCity) return;
    this.currentCity = city;
    this.afterUpdate();
  }

  // ── Profile ─────────────────────────────────────────────────────────────────

  getProfile(): HousingProfile {
    return structuredClone(this.profile);
  }

  updateProfile(patch: Partial<HousingProfile>): HousingProfile {
    this.profile = { ...this.profile, ...patch };
    this.afterUpdate();
    return this.getProfile();
  }

  getCurrentCity(): string | null {
    return this.currentCity;
  }

  getCurrentHousing(): StayListing | null {
    const listing = this.listings.find((l) => l.id === this.profile.currentHousingId);
    return listing ? structuredClone(listing) : null;
  }

  // ── Listings (manual entry; providers are future) ───────────────────────────

  addListing(input: NewListingInput, at: number = Date.now()): StayListing | null {
    if (!asString(input.title) || !asString(input.city) || !asString(input.country)) return null;
    if (input.rentMonthly != null && !(input.rentMonthly > 0)) return null;
    const listing: StayListing = {
      ...input,
      id: `stay-${at}-${this.listings.length}`,
      neighborhood: input.neighborhood ?? null,
      rentMonthly: input.rentMonthly ?? null,
      internetMbps: input.internetMbps ?? null,
      // A verification claim without a recorded speed is meaningless — drop it.
      internetVerified: input.internetVerified === true && input.internetMbps != null,
      walkabilityScore: input.walkabilityScore ?? null,
      coworkingDistanceMin: input.coworkingDistanceMin ?? null,
      safetyScore: input.safetyScore ?? null,
      transitScore: input.transitScore ?? null,
      noiseLevel: input.noiseLevel ?? null,
      communityRating: input.communityRating ?? null,
      communityRecommendations: input.communityRecommendations ?? null,
      flexibleLease: input.flexibleLease ?? null,
      available: input.available !== false,
      amenities: { ...EMPTY_AMENITIES, ...(input.amenities ?? {}) },
      source: input.source ?? 'manual',
      priceHistory: input.rentMonthly != null ? [{ rentMonthly: input.rentMonthly, at }] : [],
      addedAt: at,
      url: input.url ?? null,
    };
    this.listings.push(listing);
    if (this.listings.length > MAX_LISTINGS) this.listings = this.listings.slice(-MAX_LISTINGS);
    this.afterUpdate();
    return structuredClone(listing);
  }

  getListing(id: string): StayListing | null {
    const listing = this.listings.find((l) => l.id === id);
    return listing ? structuredClone(listing) : null;
  }

  /** Price change → history point; favorited listings get a Core notification. */
  updateListingPrice(id: string, rentMonthly: number, at: number = Date.now()): boolean {
    if (!(rentMonthly > 0)) return false;
    const listing = this.listings.find((l) => l.id === id);
    if (!listing) return false;
    const previous = listing.rentMonthly;
    if (previous === rentMonthly) return true;
    listing.rentMonthly = rentMonthly;
    listing.priceHistory.push({ rentMonthly, at });
    if (listing.priceHistory.length > MAX_PRICE_HISTORY) {
      listing.priceHistory = listing.priceHistory.slice(-MAX_PRICE_HISTORY);
    }
    if (this.favoriteListingIds.includes(id)) {
      this.publishNotification({
        kind: 'stayPriceChange',
        listingId: id,
        title: listing.title,
        previousRent: previous,
        newRent: rentMonthly,
        currency: listing.currency,
      }, at);
    }
    this.afterUpdate();
    return true;
  }

  /** Availability flip; favorited listings that become available get an alert. */
  setAvailability(id: string, available: boolean, at: number = Date.now()): boolean {
    const listing = this.listings.find((l) => l.id === id);
    if (!listing) return false;
    const was = listing.available;
    listing.available = available;
    if (!was && available && this.favoriteListingIds.includes(id)) {
      this.publishNotification({
        kind: 'stayAvailability',
        listingId: id,
        title: listing.title,
        message: `${listing.title} in ${listing.city} is available again.`,
      }, at);
    }
    this.afterUpdate();
    return true;
  }

  private publishNotification(payload: Record<string, unknown>, at: number): void {
    try {
      this.core?.publish('NotificationSent', MODULE_ID, payload, at);
    } catch {
      /* announcements are best-effort */
    }
  }

  // ── Search & match ──────────────────────────────────────────────────────────

  searchStays(filters: StaySearchFilters = {}): StayListing[] {
    const match = (l: StayListing): boolean => {
      if (filters.city != null && l.city !== filters.city) return false;
      if (filters.availableOnly === true && !l.available) return false;
      if (filters.type != null && l.type !== filters.type) return false;
      if (filters.neighborhood != null && l.neighborhood !== filters.neighborhood) return false;
      // Range filters exclude listings with UNKNOWN values only when the
      // user demands a floor/ceiling — unknown cannot honestly satisfy it.
      if (filters.maxMonthlyBudget != null && (l.rentMonthly == null || l.rentMonthly > filters.maxMonthlyBudget)) return false;
      if (filters.minInternetMbps != null && (l.internetMbps == null || l.internetMbps < filters.minInternetMbps)) return false;
      if (filters.verifiedInternetOnly === true && !l.internetVerified) return false;
      if (filters.maxCoworkingDistanceMin != null && (l.coworkingDistanceMin == null || l.coworkingDistanceMin > filters.maxCoworkingDistanceMin)) return false;
      if (filters.minSafetyScore != null && (l.safetyScore == null || l.safetyScore < filters.minSafetyScore)) return false;
      if (filters.minTransitScore != null && (l.transitScore == null || l.transitScore < filters.minTransitScore)) return false;
      if (filters.minWalkabilityScore != null && (l.walkabilityScore == null || l.walkabilityScore < filters.minWalkabilityScore)) return false;
      if (filters.minCommunityRating != null && (l.communityRating == null || l.communityRating < filters.minCommunityRating)) return false;
      if (filters.flexibleLease === true && l.flexibleLease !== true) return false;
      if (filters.quiet === true && l.noiseLevel !== 'quiet') return false;
      for (const key of ['petFriendly', 'airConditioning', 'workspace', 'balcony', 'laundry', 'kitchen', 'gym', 'pool'] as const) {
        if (filters[key] === true && l.amenities[key] !== true) return false;
      }
      return true;
    };
    return structuredClone(this.listings.filter(match));
  }

  getMatchScore(listingId: string): RoamerMatchScore | null {
    const listing = this.listings.find((l) => l.id === listingId);
    if (!listing) return null;
    return computeMatchScore(listing, this.profile);
  }

  /** Filtered listings ranked by Match Score (unscorable listings last). */
  getMatches(filters: StaySearchFilters = {}): { listing: StayListing; match: RoamerMatchScore }[] {
    const results = this.searchStays(filters).map((listing) => ({
      listing,
      match: computeMatchScore(listing, this.profile),
    }));
    results.sort((a, b) => (b.match.score ?? -1) - (a.match.score ?? -1));
    return results;
  }

  // ── Comparison ──────────────────────────────────────────────────────────────

  compareStays(listingIds: string[]): StayComparison | null {
    const listings = listingIds
      .map((id) => this.listings.find((l) => l.id === id))
      .filter((l): l is StayListing => l != null);
    if (listings.length < 2) return null;
    const matches = listings.map((l) => computeMatchScore(l, this.profile));
    const row = (label: string, pick: (l: StayListing) => string | number | null) => ({
      label,
      values: listings.map(pick),
    });
    return structuredClone({
      listingIds: listings.map((l) => l.id),
      titles: listings.map((l) => l.title),
      rows: [
        row('Rent (monthly)', (l) => l.rentMonthly),
        row('Internet (Mbps)', (l) => l.internetMbps),
        row('Internet verified', (l) => (l.internetMbps == null ? null : l.internetVerified ? 'yes' : 'no')),
        row('Neighborhood', (l) => l.neighborhood),
        row('Transit score', (l) => l.transitScore),
        row('Walkability', (l) => l.walkabilityScore),
        row('Community rating', (l) => l.communityRating),
        row('Safety score', (l) => l.safetyScore),
        row('Coworking distance (min)', (l) => l.coworkingDistanceMin),
        row('Noise', (l) => l.noiseLevel),
        row('Flexible lease', (l) => (l.flexibleLease == null ? null : l.flexibleLease ? 'yes' : 'no')),
      ],
      matchScores: matches.map((m) => m.score),
    });
  }

  // ── Housing intelligence ────────────────────────────────────────────────────

  getHousingIntelligence(city?: string): HousingIntelligence | null {
    const target = city ?? this.currentCity;
    if (!target) return null;
    return structuredClone(computeHousingIntelligence(target, this.listings));
  }

  // ── Favorites, shortlists, sharing ──────────────────────────────────────────

  favoriteListing(id: string): boolean {
    if (!this.listings.some((l) => l.id === id)) return false;
    if (!this.favoriteListingIds.includes(id)) {
      this.favoriteListingIds.push(id);
      this.afterUpdate();
    }
    return true;
  }

  unfavoriteListing(id: string): void {
    this.favoriteListingIds = this.favoriteListingIds.filter((f) => f !== id);
    this.afterUpdate();
  }

  getFavorites(): StayListing[] {
    return structuredClone(this.listings.filter((l) => this.favoriteListingIds.includes(l.id)));
  }

  favoriteNeighborhood(name: string): void {
    if (!this.favoriteNeighborhoods.includes(name)) {
      this.favoriteNeighborhoods.push(name);
      this.afterUpdate();
    }
  }

  getFavoriteNeighborhoods(): string[] {
    return [...this.favoriteNeighborhoods];
  }

  createShortlist(name: string, at: number = Date.now()): Shortlist | null {
    if (!asString(name)) return null;
    const shortlist: Shortlist = { id: `shortlist-${at}-${this.shortlists.length}`, name, listingIds: [], createdAt: at };
    this.shortlists.push(shortlist);
    if (this.shortlists.length > MAX_SHORTLISTS) this.shortlists = this.shortlists.slice(-MAX_SHORTLISTS);
    this.afterUpdate();
    return structuredClone(shortlist);
  }

  addToShortlist(shortlistId: string, listingId: string): boolean {
    const shortlist = this.shortlists.find((s) => s.id === shortlistId);
    if (!shortlist || !this.listings.some((l) => l.id === listingId)) return false;
    if (!shortlist.listingIds.includes(listingId)) shortlist.listingIds.push(listingId);
    this.afterUpdate();
    return true;
  }

  getShortlists(): Shortlist[] {
    return structuredClone(this.shortlists);
  }

  /** A shareable summary — only listing facts, never profile/budget data. */
  shareListing(id: string, at: number = Date.now()): SharedListing | null {
    const listing = this.listings.find((l) => l.id === id);
    if (!listing) return null;
    return {
      listingId: listing.id,
      title: listing.title,
      city: listing.city,
      rentMonthly: listing.rentMonthly,
      currency: listing.currency,
      url: listing.url,
      sharedAt: at,
    };
  }

  // ── Export / delete ─────────────────────────────────────────────────────────

  exportStay(now: number = Date.now()): StayExport {
    return structuredClone({
      exportedAt: now,
      profile: this.profile,
      listings: this.listings,
      favoriteListingIds: this.favoriteListingIds,
      favoriteNeighborhoods: this.favoriteNeighborhoods,
      shortlists: this.shortlists,
      stayRecords: this.stayRecords,
      stayPrivacy: this.stayPrivacy,
    });
  }

  deleteAll(): void {
    this.profile = { ...DEFAULT_HOUSING_PROFILE };
    this.currentCity = null;
    this.listings = [];
    this.favoriteListingIds = [];
    this.favoriteNeighborhoods = [];
    this.shortlists = [];
    this.stayRecords = [];
    this.stayPrivacy = { ...DEFAULT_STAY_PRIVACY };
    this.persist();
    this.emitChange();
  }

  // ── Stay records (accommodation tracking — Build 2.03.0) ────────────────────
  // Manual tracking only. Booking/hotel/Airbnb integrations do not exist and
  // are never fabricated — every record below is entered by the roamer.

  addStayRecord(input: NewStayRecordInput, at: number = Date.now()): StayRecord | null {
    if (!asString(input.propertyName) || !asString(input.city) || !asString(input.country)) return null;
    if (typeof input.checkIn !== 'number' || !Number.isFinite(input.checkIn)) return null;
    if (input.checkOut != null && input.checkOut <= input.checkIn) return null;
    if (input.price != null && !(input.price >= 0)) return null;
    const record: StayRecord = {
      ...this.sanitizeStayRecord({ ...input, id: `stayrec-${at}-${this.stayRecords.length}`, addedAt: at })!,
    };
    this.stayRecords = [...this.stayRecords, record].slice(-MAX_STAY_RECORDS);
    this.afterUpdate();
    this.publishStayRecordsChanged(at);
    return structuredClone(record);
  }

  updateStayRecord(id: string, patch: Partial<NewStayRecordInput>): StayRecord | null {
    const idx = this.stayRecords.findIndex((r) => r.id === id);
    if (idx === -1) return null;
    const merged = this.sanitizeStayRecord({ ...this.stayRecords[idx], ...patch, id, addedAt: this.stayRecords[idx].addedAt });
    if (!merged) return null;
    this.stayRecords[idx] = merged;
    this.afterUpdate();
    this.publishStayRecordsChanged(Date.now());
    return structuredClone(merged);
  }

  removeStayRecord(id: string): boolean {
    const before = this.stayRecords.length;
    this.stayRecords = this.stayRecords.filter((r) => r.id !== id);
    if (this.stayRecords.length === before) return false;
    this.afterUpdate();
    this.publishStayRecordsChanged(Date.now());
    return true;
  }

  /**
   * ECOSYSTEM SYNCHRONIZATION™ (Build 2.04.0): accommodation changes announce
   * themselves on the Core bus as a metadata-only HousingUpdated refresh
   * signal ({domain, recordCount} — never domain data). Journey™, Passport™,
   * Local™, Connect™ and the dashboard re-read from THIS module; nothing on
   * the bus duplicates stay state.
   */
  private publishStayRecordsChanged(at: number): void {
    try {
      this.core?.publish('HousingUpdated', MODULE_ID, {
        domain: 'stay-records',
        recordCount: this.stayRecords.length,
      }, at);
    } catch {
      /* bus publish must never break a local mutation */
    }
  }

  getStayRecords(): StayRecord[] {
    return structuredClone(this.stayRecords);
  }

  /** Past / current / future, each sorted by check-in date. */
  getStayTimeline(now: number = Date.now()): StayTimeline {
    const sorted = [...this.stayRecords].sort((a, b) => a.checkIn - b.checkIn);
    const current = sorted.find((r) => r.checkIn <= now && (r.checkOut == null || r.checkOut > now)) ?? null;
    return structuredClone({
      past: sorted.filter((r) => r !== current && r.checkOut != null && r.checkOut <= now),
      current,
      future: sorted.filter((r) => r.checkIn > now),
    });
  }

  /** Derived dashboard snapshot — computed at read time, never stored. */
  getStayDashboard(now: number = Date.now()): StayDashboard {
    const { current } = this.getStayTimeline(now);
    const daysRemaining = current?.checkOut != null ? Math.floor((current.checkOut - now) / DAY_MS) : null;
    const stayDurationDays = current?.checkOut != null
      ? Math.round((current.checkOut - current.checkIn) / DAY_MS)
      : null;
    return {
      currentStay: current,
      daysRemaining,
      stayDurationDays,
      extensionReminder: daysRemaining != null && daysRemaining <= 14 && daysRemaining >= 0
        ? `Check-out in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} — extend now if you plan to stay longer.`
        : null,
      moveReminder: daysRemaining != null && daysRemaining <= 7 && daysRemaining >= 0
        ? `Upcoming move: ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left at ${current!.propertyName}.`
        : null,
    };
  }

  /**
   * Base intelligence + stay history — derived exclusively from recorded
   * stays. Every unscorable value is null; totals are per-currency (recorded
   * rates only — no cross-currency conversion is ever invented).
   */
  getStayStats(now: number = Date.now()): StayStats {
    const recs = this.stayRecords;
    const finished = recs.filter((r) => r.checkOut != null) as (StayRecord & { checkOut: number })[];
    const durations = finished.map((r) => Math.round((r.checkOut - r.checkIn) / DAY_MS)).filter((d) => d > 0);
    const { current } = this.getStayTimeline(now);

    let costPerNight: number | null = null;
    let costPerMonth: number | null = null;
    if (current?.price != null && current.checkOut != null) {
      const nights = Math.round((current.checkOut - current.checkIn) / DAY_MS);
      if (nights > 0) {
        costPerNight = current.price / nights;
        costPerMonth = costPerNight * AVG_MONTH_DAYS;
      }
    }

    const totals = new Map<string, number>();
    for (const r of recs) {
      if (r.price != null) totals.set(r.currency, (totals.get(r.currency) ?? 0) + r.price);
    }

    const monthlyCosts = finished
      .filter((r) => r.price != null)
      .map((r) => {
        const nights = Math.round((r.checkOut - r.checkIn) / DAY_MS);
        return nights > 0 ? (r.price! / nights) * AVG_MONTH_DAYS : null;
      })
      .filter((c): c is number => c != null);

    // Roamer Stay Score™ — averages ONLY components the roamer actually rated
    // (1–5 scale → 0–100). Null when nothing is rated anywhere.
    const ratings = recs.flatMap((r) =>
      [r.internetQuality, r.workspaceSuitability, r.quietScore, r.walkability]
        .filter((v): v is number => v != null));

    return {
      totalStays: recs.length,
      countriesStayed: Array.from(new Set(recs.map((r) => r.country))).sort(),
      citiesStayed: Array.from(new Set(recs.map((r) => r.city))).sort(),
      longestStayDays: durations.length ? Math.max(...durations) : null,
      shortestStayDays: durations.length ? Math.min(...durations) : null,
      averageStayLengthDays: durations.length
        ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
        : null,
      costPerNight,
      costPerMonth,
      runningTotalByCurrency: Array.from(totals, ([currency, total]) => ({ currency, total })),
      averageMonthlyHousingCost: monthlyCosts.length
        ? monthlyCosts.reduce((a, b) => a + b, 0) / monthlyCosts.length
        : null,
      stayScore: ratings.length
        ? Math.round(((ratings.reduce((a, b) => a + b, 0) / ratings.length) / 5) * 100)
        : null,
    };
  }

  getStayPrivacy(): StayPrivacy {
    return { ...this.stayPrivacy };
  }

  updateStayPrivacy(patch: Partial<StayPrivacy>): StayPrivacy {
    this.stayPrivacy = {
      shareStayHistory: patch.shareStayHistory ?? this.stayPrivacy.shareStayHistory,
      shareCurrentLocation: patch.shareCurrentLocation ?? this.stayPrivacy.shareCurrentLocation,
    };
    this.afterUpdate();
    return this.getStayPrivacy();
  }

  /** Record-by-record sanitizer shared by mutations AND the load path. */
  private sanitizeStayRecord(r: Record<string, unknown>): StayRecord | null {
    const propertyName = asString(r.propertyName);
    const city = asString(r.city);
    const country = asString(r.country);
    const checkIn = asNumberOrNull(r.checkIn);
    if (!propertyName || !city || !country || checkIn == null) return null;
    const checkOut = asNumberOrNull(r.checkOut);
    const rating = (v: unknown): number | null => {
      const n = asNumberOrNull(v);
      return n != null && n >= 1 && n <= 5 ? n : null;
    };
    const price = asNumberOrNull(r.price);
    return {
      id: asString(r.id) ?? `stayrec-${checkIn}-0`,
      propertyName,
      type: asString(r.type) ?? 'other',
      city,
      country,
      address: asString(r.address),
      host: asString(r.host),
      bookingRef: asString(r.bookingRef),
      price: price != null && price >= 0 ? price : null,
      currency: asString(r.currency) ?? 'USD',
      paymentStatus: PAYMENT_STATUSES.includes(r.paymentStatus as never) ? (r.paymentStatus as StayRecord['paymentStatus']) : null,
      checkIn,
      checkOut: checkOut != null && checkOut > checkIn ? checkOut : null,
      internetQuality: rating(r.internetQuality),
      workspaceSuitability: rating(r.workspaceSuitability),
      quietScore: rating(r.quietScore),
      walkability: rating(r.walkability),
      safetyNotes: asString(r.safetyNotes),
      personalNotes: asString(r.personalNotes),
      addedAt: asNumberOrNull(r.addedAt) ?? 0,
    };
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        profile: this.profile,
        currentCity: this.currentCity,
        listings: this.listings,
        favoriteListingIds: this.favoriteListingIds,
        favoriteNeighborhoods: this.favoriteNeighborhoods,
        shortlists: this.shortlists,
        stayRecords: this.stayRecords,
        stayPrivacy: this.stayPrivacy,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('stay', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Called by hydrateStayFromCloud when a newer cloud copy is available. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      const p = (state.profile ?? {}) as Record<string, unknown>;
      this.profile = {
        currentHousingId: asString(p.currentHousingId),
        monthlyBudget: asNumberOrNull(p.monthlyBudget),
        budgetCurrency: asString(p.budgetCurrency) ?? 'USD',
        preferredType: typeof p.preferredType === 'string' ? p.preferredType : null,
        leaseLengthMonths: asNumberOrNull(p.leaseLengthMonths),
        moveInDate: asNumberOrNull(p.moveInDate),
        moveOutDate: asNumberOrNull(p.moveOutDate),
        hasPets: p.hasPets === true,
        smokingPreference: p.smokingPreference === 'nonSmoking' || p.smokingPreference === 'smoking'
          ? p.smokingPreference
          : 'noPreference',
        requiresWorkspace: p.requiresWorkspace === true,
        minInternetMbps: asNumberOrNull(p.minInternetMbps),
        preferredNeighborhoods: (Array.isArray(p.preferredNeighborhoods) ? p.preferredNeighborhoods : [])
          .filter((n): n is string => typeof n === 'string'),
      };
      this.currentCity = asString(state.currentCity);

      this.listings = (Array.isArray(state.listings) ? state.listings : [])
        .filter((l): l is Record<string, unknown> =>
          l != null && typeof l === 'object' &&
          typeof (l as { id?: unknown }).id === 'string' &&
          typeof (l as { title?: unknown }).title === 'string' &&
          typeof (l as { city?: unknown }).city === 'string' &&
          typeof (l as { type?: unknown }).type === 'string')
        .map((l): StayListing => {
          const a = (l.amenities ?? {}) as Record<string, unknown>;
          const amenities: StayAmenities = { ...EMPTY_AMENITIES };
          for (const key of Object.keys(EMPTY_AMENITIES) as (keyof StayAmenities)[]) {
            amenities[key] = typeof a[key] === 'boolean' ? (a[key] as boolean) : null;
          }
          const rent = asNumberOrNull(l.rentMonthly);
          return {
            id: l.id as string,
            title: l.title as string,
            type: l.type as string,
            city: l.city as string,
            country: asString(l.country) ?? '',
            neighborhood: asString(l.neighborhood),
            rentMonthly: rent != null && rent > 0 ? rent : null,
            currency: asString(l.currency) ?? 'USD',
            internetMbps: asNumberOrNull(l.internetMbps),
            internetVerified: l.internetVerified === true && asNumberOrNull(l.internetMbps) != null,
            walkabilityScore: asNumberOrNull(l.walkabilityScore),
            coworkingDistanceMin: asNumberOrNull(l.coworkingDistanceMin),
            safetyScore: asNumberOrNull(l.safetyScore),
            transitScore: asNumberOrNull(l.transitScore),
            noiseLevel: l.noiseLevel === 'quiet' || l.noiseLevel === 'moderate' || l.noiseLevel === 'loud' ? l.noiseLevel : null,
            communityRating: asNumberOrNull(l.communityRating),
            communityRecommendations: asNumberOrNull(l.communityRecommendations),
            flexibleLease: typeof l.flexibleLease === 'boolean' ? l.flexibleLease : null,
            available: l.available !== false,
            amenities: amenities as StayAmenities,
            source: l.source === 'provider' || l.source === 'community' ? l.source : 'manual',
            priceHistory: (Array.isArray(l.priceHistory) ? l.priceHistory : [])
              .filter((pp): pp is { rentMonthly: number; at: number } =>
                pp != null && typeof pp === 'object' &&
                typeof (pp as { rentMonthly?: unknown }).rentMonthly === 'number' &&
                typeof (pp as { at?: unknown }).at === 'number')
              .slice(-MAX_PRICE_HISTORY),
            addedAt: asNumberOrNull(l.addedAt) ?? 0,
            url: asString(l.url),
          };
        })
        .slice(-MAX_LISTINGS);

      const listingIds = new Set(this.listings.map((l) => l.id));
      this.favoriteListingIds = (Array.isArray(state.favoriteListingIds) ? state.favoriteListingIds : [])
        .filter((id): id is string => typeof id === 'string' && listingIds.has(id));
      this.favoriteNeighborhoods = (Array.isArray(state.favoriteNeighborhoods) ? state.favoriteNeighborhoods : [])
        .filter((n): n is string => typeof n === 'string');
      this.shortlists = (Array.isArray(state.shortlists) ? state.shortlists : [])
        .filter((s): s is Shortlist =>
          s != null && typeof s === 'object' &&
          typeof (s as Shortlist).id === 'string' &&
          typeof (s as Shortlist).name === 'string' &&
          Array.isArray((s as Shortlist).listingIds))
        .map((s) => ({ ...s, listingIds: s.listingIds.filter((id) => typeof id === 'string' && listingIds.has(id)) }))
        .slice(-MAX_SHORTLISTS);

      this.stayRecords = (Array.isArray(state.stayRecords) ? state.stayRecords : [])
        .filter((r): r is Record<string, unknown> => r != null && typeof r === 'object')
        .map((r) => this.sanitizeStayRecord(r))
        .filter((r): r is StayRecord => r != null)
        .slice(-MAX_STAY_RECORDS);

      const sp = (state.stayPrivacy ?? {}) as Record<string, unknown>;
      this.stayPrivacy = {
        shareStayHistory: sp.shareStayHistory === true,
        shareCurrentLocation: sp.shareCurrentLocation === true,
      };
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerStayModule = new RoamerStayModule();

/**
 * Boot hydration: fetches the cloud copy of the Stay™ state if it is
 * strictly newer than the last local write, writes it to localStorage, and
 * signals the module to reload.  Fire-and-forget — boot never blocks the network.
 */
export async function hydrateStayFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<unknown>('stay', 'state');
  if (!cloud) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud));
    }
  } catch { return false; }
  roamerStayModule.reloadFromStorage();
  return true;
}

// Referenced to avoid unused warnings until providers ship.
void HOUSING_TYPES;
